import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { RideStatusIndicator } from "@ryde/controls";
import {
  RideInvestigationHistoryOriginEnum,
  useFormats,
  type IRideInvestigationHistoryEntryDto,
} from "@ryde/shared";

import { INVESTIGATION_HISTORY } from "../investigation.constants";
import type { IInvestigationAuditChanges } from "../investigation.models";
import { InvestigationHistoryAuditChanges } from "./investigation.history-audit-changes";

interface IInvestigationHistoryEntryDetailsProps {
  entry: IRideInvestigationHistoryEntryDto;
  /** What an audited change did in the ride history drawer's terms; without it the raw changed fields are listed. */
  auditChanges?: IInvestigationAuditChanges;
}

/** The source-specific lines under a History row: who, what changed, which job, which supplier call. */
export const InvestigationHistoryEntryDetails: FC<
  IInvestigationHistoryEntryDetailsProps
> = ({ entry, auditChanges }) => {
  const { t } = useTranslation();
  const { formatDateTimeWithSecondsNormalized } = useFormats();
  const { creation, audit, process, supplier, passenger } = entry;
  const key = INVESTIGATION_HISTORY.i18nKey;

  return (
    <>
      {creation && (
        <div className="history-line">
          {creation.origin !== RideInvestigationHistoryOriginEnum.Unknown && (
            <span>
              {t(`${key}.origin.${creation.origin}`, {
                id: creation.productionId ?? creation.courseId,
              })}
            </span>
          )}
          {creation.journeyLegNumber !== undefined && (
            <span>
              {t(`${key}.journeyLeg`, {
                leg: creation.journeyLegNumber,
                total: creation.journeyLegsCount,
                id: creation.journeyId,
              })}
            </span>
          )}
        </div>
      )}
      {auditChanges && (
        <InvestigationHistoryAuditChanges changes={auditChanges.changes} />
      )}
      {!auditChanges && audit?.status && (
        <div className="history-line">
          <RideStatusIndicator status={audit.status} />
        </div>
      )}
      {!auditChanges && !!audit?.changedFields.length && (
        <div className="history-line">
          {t(`${key}.changedFields`, {
            fields: audit.changedFields.join(", "),
          })}
        </div>
      )}
      {process && (
        <div className="history-line">
          <span>{t(`process.${process.title}`)}</span>
          {process.total !== undefined && (
            <span>{t(`${key}.process.records`, { count: process.total })}</span>
          )}
          {!!process.failedCount && (
            <span>
              {t(`${key}.process.failed`, { count: process.failedCount })}
            </span>
          )}
          {process.hasFailedForRide && (
            <span className="history-badge history-badge--critical">
              {t(`${key}.process.failedForRide`)}
            </span>
          )}
          {entry.endTimestamp && (
            <span>
              {t(`${key}.process.ranUntil`, {
                time: formatDateTimeWithSecondsNormalized(entry.endTimestamp),
              })}
            </span>
          )}
        </div>
      )}
      {supplier && (
        <div className="history-line">
          <span>
            {[supplier.provider, supplier.flowAction]
              .filter(Boolean)
              .join(" · ")}
          </span>
          {supplier.httpStatus !== undefined && (
            <span className="history-badge">
              {t(`${key}.supplier.httpStatus`, { status: supplier.httpStatus })}
            </span>
          )}
        </div>
      )}
      {passenger?.passengerName && (
        <div className="history-line">{passenger.passengerName}</div>
      )}
      {(entry.actorName || entry.detail) && (
        <div className="history-line">
          {entry.actorName && (
            <span>{t(`${key}.by`, { name: entry.actorName })}</span>
          )}
          {entry.detail && <span>{entry.detail}</span>}
        </div>
      )}
    </>
  );
};
